import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Label } from '@clarix/ui';
import { useCheckSlug, useCreateWorkspace, useMasters, useMe, normalizeError } from '@/core/api';
import { buildWorkspaceUrl } from '@/gateway/getWorkspaceSlug';
import { WorkspaceTransitionOverlay } from './WorkspaceTransitionOverlay';

/**
 * Minimal workspace claim — the only step before the full-canvas /setup wizard.
 *
 * Captures just the two things that must exist before a workspace can be
 * routed to its own subdomain:
 *   1. Workspace name (identity)
 *   2. Slug           (URL routing)
 *
 * Everything the old form asked for — industry, team size, org type, timezone,
 * currency, branding, billing, work calendar — now lives ONLY in the full-canvas
 * /setup wizard (CONFIGURATION step), so nothing is asked twice. The few fields
 * the create endpoint still requires (timezone, locale, currency, industry,
 * team_size) are filled in automatically here: timezone/locale from the browser
 * and the rest from sensible master defaults. The user reviews and edits every
 * one of them in /setup immediately after.
 */

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

const SLUG_DEBOUNCE_MS = 350;

/** Red asterisk marking a required field, rendered inside its <Label>. */
function RequiredMark() {
  return (
    <span className="ml-0.5 text-red-500" aria-hidden="true">
      *
    </span>
  );
}

export function OnboardingPage() {
  const me = useMe();
  const create = useCreateWorkspace();

  // Masters are NOT shown on this step anymore — they only supply the defaults
  // the create endpoint still requires. The user picks the real values in the
  // full-canvas /setup wizard right after.
  const industries = useMasters('industry');
  const teamSizes = useMasters('team_size_range');
  const currencies = useMasters('currency');

  // A user may belong to exactly one organization. If they're already a member
  // of any workspace, they cannot create another one — bounce them to it. We
  // key off the workspaces array (the source of truth for membership) rather
  // than next_step alone, and fall back to the first workspace if the backend
  // didn't pin an active slug.
  const memberWorkspaces = me.data?.workspaces ?? [];
  const alreadyHasWorkspace = memberWorkspaces.length > 0;
  const redirectSlug = me.data?.active_workspace_slug ?? memberWorkspaces[0]?.slug ?? null;

  useEffect(() => {
    if (alreadyHasWorkspace && redirectSlug) {
      window.location.replace(buildWorkspaceUrl(redirectSlug, '/'));
    }
  }, [alreadyHasWorkspace, redirectSlug]);

  // Identity (visible). The single name field seeds both the workspace display
  // name and the legal company name; they can diverge later in Settings.
  const [companyName, setCompanyName] = useState('');

  const [slugState, setSlugState] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const derivedSlug = useMemo(() => slugify(companyName), [companyName]);
  const slug = slugDirty ? slugState : derivedSlug;
  const [debouncedSlug, setDebouncedSlug] = useState(slug);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSlug(slug), SLUG_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [slug]);
  const slugCheck = useCheckSlug(debouncedSlug);

  const slugStatus: 'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid' =
    !debouncedSlug || debouncedSlug.length < 2
      ? 'idle'
      : slugCheck.isFetching
        ? 'checking'
        : slugCheck.data
          ? slugCheck.data.available
            ? 'available'
            : slugCheck.data.reason === 'TAKEN'
              ? 'taken'
              : slugCheck.data.reason === 'RESERVED'
                ? 'reserved'
                : 'invalid'
          : 'idle';

  const slugHint =
    slugStatus === 'checking'
      ? 'Checking availability…'
      : slugStatus === 'available'
        ? 'Available'
        : slugStatus === 'taken'
          ? 'That URL is already taken.'
          : slugStatus === 'reserved'
            ? 'That URL is reserved.'
            : slugStatus === 'invalid'
              ? 'Use lowercase letters, digits, and hyphens. 2 – 63 chars.'
              : 'Lowercase letters, digits, and hyphens. 2 – 63 characters.';

  // Auto-detected defaults for the fields the create endpoint still requires but
  // we no longer ask for. The user refines all of these in /setup.
  const autoLocale = useMemo(
    () => (typeof navigator !== 'undefined' && navigator.language) || 'en-IN',
    [],
  );
  const autoTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    } catch {
      return 'Asia/Kolkata';
    }
  }, []);

  // Keeps the full-screen transition overlay mounted from submit start through
  // the cross-subdomain redirect, so the gap never reads as a blank page.
  const [redirecting, setRedirecting] = useState(false);

  // Master-derived defaults must be loaded before we can satisfy the create
  // contract with valid codes, so gate submit on them alongside the slug check.
  const defaultsReady = !!industries.data && !!teamSizes.data && !!currencies.data;

  const slugOk = slugStatus === 'available';
  const canSubmit =
    !create.isPending &&
    !alreadyHasWorkspace &&
    companyName.trim().length >= 2 &&
    slug.trim().length >= 2 &&
    slugOk &&
    defaultsReady;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Hard stop: never create a second workspace for a user who already belongs
    // to one, even if the form was somehow submitted (stale state, etc.).
    if (alreadyHasWorkspace || !canSubmit) return;
    const name = companyName.trim();
    try {
      const data = await create.mutateAsync({
        name,
        company_name: name,
        slug: slug.trim(),
        // Required by the create contract but configured for real in /setup —
        // seed with sensible defaults so nothing is asked twice here.
        industry: industries.data?.rows[0]?.code ?? 'unspecified',
        team_size: teamSizes.data?.rows[0]?.code ?? 'unspecified',
        timezone: autoTimezone,
        locale: autoLocale,
        currency: currencies.data?.rows[0]?.code ?? 'INR',
      });
      // Land on /setup so the user walks through configuration → masters before
      // getting to the workspace home. Token is forwarded via URL hash so the
      // new subdomain origin can pick it up. /setup is chrome-free.
      const target = buildWorkspaceUrl(data.workspace.slug, '/setup');
      setRedirecting(true);
      window.location.assign(`${target}#token=${encodeURIComponent(data.access_token)}`);
    } catch {
      /* error rendered below */
    }
  };

  // Keep the overlay up (instead of flashing the create form) while we redirect
  // an already-onboarded member to their existing workspace.
  const showOverlay = create.isPending || redirecting || alreadyHasWorkspace;

  const error = create.error ? normalizeError(create.error) : null;

  return (
    <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-6 shadow-xl">
      {showOverlay && <WorkspaceTransitionOverlay />}
      <h1 className="text-4xl font-semibold text-slate-900 dark:text-slate-100">
        Create your workspace
      </h1>
      <p className="mt-1 text-lg text-slate-500">
        Name it and claim a URL — you&apos;ll configure everything else in the setup that follows.
      </p>

      <form className="mt-5 grid grid-cols-1 gap-y-3 [&_label]:text-lg" onSubmit={submit} noValidate>
        {/* ---------- Identity ---------- */}
        <div>
          <Label htmlFor="company_name">
            Workspace name
            <RequiredMark />
          </Label>
          <Input
            id="company_name"
            type="text"
            placeholder="Sarvadhi Solutions Pvt Ltd"
            autoFocus
            required
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="slug">
            Workspace URL
            <RequiredMark />
          </Label>
          <div className="flex overflow-hidden rounded-md ring-1 ring-slate-300 focus-within:ring-2 focus-within:ring-indigo-500 dark:ring-slate-700">
            <Input
              id="slug"
              type="text"
              pattern="^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$"
              required
              placeholder={derivedSlug || 'your-workspace'}
              value={slug}
              onChange={(e) => {
                setSlugState(e.target.value);
                setSlugDirty(true);
              }}
              className="rounded-none border-0 ring-0 focus:ring-0"
            />
            <span className="flex items-center bg-slate-50 px-2 text-lg text-slate-500 dark:bg-slate-900">
              .clarix.com
            </span>
          </div>
          <p
            className={`mt-1 text-sm leading-tight ${
              slugStatus === 'available'
                ? 'text-emerald-600 dark:text-emerald-400'
                : slugStatus === 'taken' || slugStatus === 'reserved' || slugStatus === 'invalid'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-slate-500'
            }`}
          >
            {slugHint}
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-lg text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {error.message}
          </div>
        )}

        <div className="mt-1">
          <Button type="submit" className="w-full" disabled={!canSubmit}>
            {create.isPending ? 'Creating workspace…' : 'Create workspace'}
          </Button>
          <p className="mt-2 text-center text-sm text-slate-400">
            Next: a quick guided setup for industry, locale, currency, and your workspace lists.
          </p>
        </div>
      </form>
    </div>
  );
}
